import {Component, OnInit, OnDestroy, AfterContentInit,
	ViewChild}                                      from '@angular/core';
import {ActivatedRoute, Router}                     from "@angular/router";
import {HttpErrorResponse}                          from "@angular/common/http";

import {AngularQuery}                               from "../../services/query/angularquery";
import {Message}                                    from "../../services/message/message";
import {MessageEntry}                               from "../../services/message/message-entry";
import {MessageService}                             from "../../services/message/message.service";
import {News}                                       from "../../services/news/news";
import {NewsService}                                from "../../services/news/news-service";
import {PageSettings}                               from "../../services/news/page-settings";
import {Permissions}                                from "../../services/permissions/permissions";
import {Player}                                     from "../../services/player/player";
import {Query}                                      from "../../services/query/query";
import {Source}                                     from "../../services/source/source";
import {SourceService}                              from "../../services/source/source.service";
import {Sport}                                      from '../../services/sport/sport';
import {SportService}                               from '../../services/sport/sport.service';
import {Team}                                       from "../../services/team/team";
import {User}                                       from "../../services/user/user";

import {BehaviorSubject}                            from "rxjs/BehaviorSubject";
import {isNullOrUndefined}                          from "util";
import {Subscription}                               from "rxjs";


@Component({
	templateUrl: './list.component.html',
	styleUrls: ['./list.component.css']
})


/**
 * This component handles the searching for and selection
 * of articles for editing.
 *
 */
export class ListComponent implements OnInit, AfterContentInit, OnDestroy {

	// Display page variables.
	currentPage: number = 1;                        // The current page of search results being displayed.
	lastSearchType: string = 'clear';               // The most recent search type.  Types are 'last', 'clear', 'title', 'story_id', 'player', 'team', 'source'.
	perPageChoice: number = 15;                     // The initial choice for the number of search results to display.
	perPageChoices: number[] = [ 10, 15, 20, 25, 50, 100 ]; // The choices for the number of search results to display.
	searchMessage:string = '';                      // The message displayed above the search results.
	searchOrderField: string = 'date';              // Which field are we searching by?
	searchSortOrder: string = 'desc';               // Which direction should the search field by ordered by ('desc' or 'asc')?

	// ARTICLE (NEWS) variables.
	news: News[];                                   // The Observable array of News items found via query.
	newsSubscription: Subscription;                 // The subscription to the new array.
	searchNews: BehaviorSubject<string>;            // Used by the News query; initialized in the constructor.

	// MESSAGE variables.
	private message: Message;
	msgSubscription: Subscription;                  // Used by the MessageService.

	// SEARCH variables.
	playerSearchId: number = -1;                    // The player selected for a player search.
	queryErrors: boolean = false;                   // Have there been any query errors?
	queryMessage: MessageEntry;                     // The error message associated with the query error.
	searchStatus: number = 3;                       // The current search status. Trash (-1) Draft (0), Publish (1), Future (2), All (3).
	sourceSearchId:number = -1;                     // The ID of the source selected in a source search.
	teamSearchId: number = -1;                      // The team selected for a team search.

	// SOURCE variables.
	sourceSubscription: Subscription;               // The Subscription for the Source service.

	// SPORT variables.
	currentItemSport: string = 'NFL';               // DO NOT USE THIS VARIABLE FOR ANYTHING.
	currentSport: string = 'None';                  // The current Sport radio button value.
	sports: Sport[];                                // The array of information about each Sport choice.
	sptSubscription: Subscription;                  // The Subscription for the Sports service.

	// USER variables.
	users:User[];                                   // User information needed for lookups.
	isAdministrator:boolean = false;                // Whether or not the current user is an administrator.

	/**
	 * Initialize the objects that supply the information
	 * this editor can work with.
	 *
	 * @param {MessageService} msgSvc        The MessageService singleton.
	 * @param {NewsService}    newsService   The NewsService singleton.
	 * @param {ActivatedRoute} route         Used when retrieving the info from SportsResolve and UserResolve.
	 * @param {Router}         router        The Router singleton.
	 * @param {SourceService}  sourceService The SourceService singleton.
	 * @param {SportService}   sportService  The SportService singleton.
	 */
	constructor(

		private msgSvc: MessageService,
        private newsService: NewsService,
		private route: ActivatedRoute,
        private router: Router,
		private sourceService: SourceService,
		private sportService: SportService,

	) {}


	/**
	 * This runs once to initialize the data services.
	 */
	ngOnInit() {

		this.searchNews = new BehaviorSubject<string>(this.getQuery('clear')); // Initialize the News query.
		this.msgSubscription = this.msgSvc.subject.subscribe( message => { this.message = message; } );

		// Initialize the User table.
		this.users = this.route.snapshot.data['users'];

		// Mark the current user as an administrator or non-administrator.
		this.isAdministrator = Permissions.checkPermission(Permissions.SUPER_USER);

		// Set page defaults, if there are any.
		let ps:string = sessionStorage.getItem('pageSettings') || '{}';
		let settings:PageSettings = new PageSettings(JSON.parse(ps));

		if (! settings.initialized) {

			// Persistent choices.
			settings.perPageChoice = settings.perPageChoice ? settings.perPageChoice : this.perPageChoice;

			// Non-persistent choices.
			settings.currentPage = this.currentPage;
			settings.currentSport = this.currentSport;
			settings.lastSearchType = this.lastSearchType;
			settings.playerSearchId = this.playerSearchId;
			settings.searchByStoryId = '';
			settings.searchFullText = '';
			settings.searchOrderField = this.searchOrderField;
			settings.searchSortOrder = this.searchSortOrder;
			settings.searchStatus = this.searchStatus;
			settings.teamSearchId = this.teamSearchId;
			settings.typeaheadPlayerName = '';
			settings.typeaheadSource = '';
			settings.typeaheadTeamname = '';
			settings.startDate = '';
			settings.endDate = '';
			settings.currentItemSport = '';

			settings.initialized = true;
			sessionStorage.setItem('pageSettings', JSON.stringify(settings));

			// Ask the NewsService to search for articles and return the results.
			this.searchArticles('clear', false);

		} else {

			this.currentPage = settings.currentPage;
			this.currentSport = settings.currentSport;
			this.lastSearchType = settings.lastSearchType;
			this.perPageChoice = settings.perPageChoice;
			this.playerSearchId = settings.playerSearchId;
			this.searchByStoryId.nativeElement.value = settings.searchByStoryId;
			this.searchFullText.nativeElement.value = settings.searchFullText;
			this.searchOrderField = settings.searchOrderField;
			this.searchSortOrder = settings.searchSortOrder;
			this.searchStatus = settings.searchStatus;
			this.teamSearchId = settings.teamSearchId;
			this.playerTypeahead.typeaheadPlayerNameInput.nativeElement.value = settings.typeaheadPlayerName;
			this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = settings.typeaheadSource;
			this.teamTypeahead.typeaheadTeamInput.nativeElement.value = settings.typeaheadTeamname;
			this.startDate.nativeElement.value = settings.startDate;
			this.endDate.nativeElement.value = settings.endDate;
			this.currentItemSport = settings.currentItemSport;

			// Ask the NewsService to search for articles and return the results.
			this.searchArticles('last', false);

		}

	}

	/**
	 * This runs once, after content is initialized,
	 * to create the list of Sports in the search window.
	 *
	 */
	ngAfterContentInit() {

		this.sptSubscription = this.sportService.getSports().subscribe(

			(sports) => this.sports = sports,

		);

	}


	/**
	 * Unsubscribe from subscriptions to avoid memory leaks.
	 */
	ngOnDestroy() {

		if (this.msgSubscription) {
			this.msgSubscription.unsubscribe();
		}

		if (this.newsSubscription) {
			this.newsSubscription.unsubscribe();
		}

		if (this.sourceSubscription) {
			this.sourceSubscription.unsubscribe();
		}

		if (this.sptSubscription) {
			this.sptSubscription.unsubscribe();
		}

	}

	/**
	 * Link to the end date field.
	 */
	@ViewChild('endDate')
	endDate:any;


	/**
	 * Link to the player typeahead.
	 */
	@ViewChild('playerTypeahead')
	playerTypeahead:any;


	/**
	 * Link to the story ID search box.
	 */
	@ViewChild('searchByStoryId')
	searchByStoryId:any;


	/**
	 * Link to the full-text search box.
	 */
	@ViewChild('searchFullText')
	searchFullText:any;


	/**
	 * Link to the source typeahead.
	 */
	@ViewChild('sourceTypeahead')
	sourceTypeahead:any;


	/**
	 * Link to the start date field.
	 */
	@ViewChild('startDate')
	startDate:any;


	/**
	 * Link to the team typeahead.
	 */
	@ViewChild('teamTypeahead')
	teamTypeahead:any;


	/**
	 * This handles click action on the 'Clear' button.
	 *
	 */
	clearButton(): void {

		this.currentPage = 1;
		this.searchStatus = 3;
		this.currentSport = 'None';
		this.searchByStoryId.nativeElement.value = '';
		this.searchFullText.nativeElement.value = '';
		this.playerTypeahead.typeaheadPlayerNameInput.nativeElement.value = '';
		this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = '';
		this.teamTypeahead.typeaheadTeamInput.nativeElement.value = '';
		this.startDate.nativeElement.value = '';
		this.endDate.nativeElement.value = '';
		this.playerSearchId = -1;
		this.sourceSearchId = -1;
		this.teamSearchId = -1;
		this.searchArticles('clear', true);

	}


	/**
	 * Target for UI buttons that clear the selected search field.
	 * @param {string} name The field to clear.
	 */
	clearField(name:string):void {

		switch(name) {

			case 'date':
				this.startDate.nativeElement.value = '';
				this.endDate.nativeElement.value = '';
				break;

			case 'player':
				this.playerTypeahead.typeaheadPlayerNameInput.nativeElement.value = '';
				this.playerSearchId = -1;
				break;

			case 'source':
				this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = '';
				this.sourceSearchId = -1;
				break;

			case 'team':
				this.teamTypeahead.typeaheadTeamInput.nativeElement.value = '';
				this.teamSearchId = -1;
				break;

		}

	}


	/**
	 * Check the start and end date field values and make sure
	 * they contain valid content.
	 *
	 * @return {boolean} True if there are dates, false if not.
	 */
	datesValid():boolean {

		if (isNullOrUndefined(this.startDate.nativeElement.value)) {
			return false;
		}
		if (isNullOrUndefined(this.endDate.nativeElement.value)) {
			return false;
		}
		if (0 === this.startDate.nativeElement.value.length) {
			return false;
		}
		if (0 === this.endDate.nativeElement.value.length) {
			return false;
		}

		return true;

	}

	/**
	 * Used by list.component.html to delete
	 * the story specified by the provided Angular
	 * ReST API "news" table ID value.
	 *
	 * @param {int} id  The ReST API ID of the article to be deleted.
	 */
	deleteArticle( id: number ): void {

		// Give them the option to cancel.
		if (! window.confirm('Are you sure you want to delete #' + String(id) + '?')) {
			return;
		}

		this.savePageSettings();

		this.newsSubscription = this.newsService.deleteItem(id).subscribe(
			() => {

				// Refresh the search, since the delete succeeded if we got here.
				this.searchArticles(this.lastSearchType, false);

			},
			(error:any) => {

				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: 'Delete failed due to error: ' + Query.getQueryErrorMessage(error),
					level: 4

				}));

			}
		);

	}

	/**
	 * Re-direct the view to the edit
	 * page for the specified article
	 * based on the provided Angular ReST
	 * API "news" table ID value.
	 *
	 * @param {int} id  The ReST API ID of the article to be edited.
	 */
	editArticle( id: number ): void {

		this.savePageSettings();
		this.router.navigate(['/articles/item', id]);

	}


	/**
	 * Create an AngularQuery object based
	 * on the current settings, then return
	 * the URL for the query described.
	 *
	 * @param type   The type of search requested ('clear', 'last', 'story_id', 'fulltext').
	 * @returns {string} A query string based on the type and data read.
	 */
	getQuery(type:string): string {

		// A choice of 'last' for the query means it's the same as the last query type.
		if ('last' === type) {

			type = this.lastSearchType;

		} else {

			this.lastSearchType = type;

		}

		// These are settings common to all queries.
		let query: AngularQuery = new AngularQuery();
		query.setPerPage(this.perPageChoice);
		query.setPage(this.currentPage - 1);
		query.addParameter('order_field', this.searchOrderField);
		query.addParameter('sort_order', this.searchSortOrder);
		let queryLimits:string[] = [];

		// Handle a query that is limited to a specific status, or return all but Trashed stories.
		if (3 !== this.searchStatus) {

			query.addParameter('visible', this.searchStatus.toString());
			queryLimits.push('Status');

		} else {

			query.addParameter('visible', '0'); // Draft
			query.addParameter('visible', '1'); // Published
			query.addParameter('visible', '2'); // Future

		}

		// Handle a query that is limited by a specific sport.
		if ('None' !== this.currentSport) {

			query.addParameter('code', this.currentSport);
			queryLimits.push('Sport');

		}

		// Handle a query that is limited by dates.
		if (this.datesValid()) {

			query.addParameter('startdate', this.startDate.nativeElement.value);
			query.addParameter('enddate', this.endDate.nativeElement.value);
			queryLimits.push('Date');

		}

		// Add settings based on the query type.
		switch(type) {

			case 'clear':

				query.setEndpoint(AngularQuery.ENDPOINT_NEWS_SEARCH);

				// if (! isNullOrUndefined(this.searchByStoryId)) {
				//
				// 	this.searchByStoryId.nativeElement.value = '';
				//
				// }
				//
				// if (! isNullOrUndefined(this.searchFullText)) {
				//
				// 	this.searchFullText.nativeElement.value = '';
				//
				// }

				// You can search by player or team, but not both.
				if (-1 !== this.playerSearchId) {

					query.addParameter('player_id', this.playerSearchId.toString());
					queryLimits.push('Player');

				} else if (-1 !== this.teamSearchId) {

					query.addParameter('team_id', this.teamSearchId.toString());
					queryLimits.push('Team');

				}

				if (-1 !== this.sourceSearchId) {

					query.addParameter('source_id', this.sourceSearchId.toString());
					queryLimits.push('Source');

				}

				break;

			case 'story_id':

				query.setEndpoint(AngularQuery.ENDPOINT_NEWS);

				if (! isNullOrUndefined(this.searchByStoryId)) {

					let pattern:RegExp = /^\d+$/;

					// Make sure it's all digits.
					if (null !== this.searchByStoryId.nativeElement.value.trim().match(pattern)) {

						query.addParameter('id', this.searchByStoryId.nativeElement.value.trim());
						query.removeParameter('visible');
						query.removeParameter('code');
						this.searchStatus = 3;
						this.currentSport = 'None';
						this.searchFullText.nativeElement.value = '';
						this.playerTypeahead.typeaheadPlayerNameInput.nativeElement.value = '';
						this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = '';
						this.teamTypeahead.typeaheadTeamInput.nativeElement.value = '';
						this.startDate.nativeElement.value = '';
						this.endDate.nativeElement.value = '';
						this.playerSearchId = -1;
						this.sourceSearchId = -1;
						this.teamSearchId = -1;

						queryLimits = [];
						this.lastSearchType = 'story_id';
						queryLimits.push('Story ID');

					} else {

						// Return an error condition.
						this.queryMessage = new MessageEntry({

							message: 'Search did not complete because the story ID must be all digits',
							level: 4

						});
						this.lastSearchType = 'clear';
						this.queryErrors = true;

					}

				} else {

					// Return an error condition.
					this.queryMessage = new MessageEntry({

						message: 'Search did not complete because the story ID was invalid',
						level: 4

					});
					this.lastSearchType = 'clear';
					this.queryErrors = true;

				}

				break;

			case 'fulltext':

				if (! isNullOrUndefined(this.searchFullText) && 0 !== this.searchFullText.nativeElement.value.trim().length) {

					query.setEndpoint(AngularQuery.ENDPOINT_NEWS_SEARCH_FULLTEXT, this.searchFullText.nativeElement.value);
					query.removeParameter('visible');
					query.removeParameter('code');
					this.searchStatus = 3;
					this.currentSport = 'None';
					this.searchByStoryId.nativeElement.value = '';
					this.playerTypeahead.typeaheadPlayerNameInput.nativeElement.value = '';
					this.sourceTypeahead.typeaheadSourceInput.nativeElement.value = '';
					this.teamTypeahead.typeaheadTeamInput.nativeElement.value = '';
					this.startDate.nativeElement.value = '';
					this.endDate.nativeElement.value = '';
					this.playerSearchId = -1;
					this.sourceSearchId = -1;
					this.teamSearchId = -1;

					queryLimits = [];
					this.lastSearchType = 'fulltext';
					queryLimits.push('Full Text');

				} else {

					// Return an error condition.
					this.queryMessage = new MessageEntry({

						message: 'Search did not complete because no search was specified',
						level: 4

					});
					this.lastSearchType = 'clear';
					this.queryErrors = true;

				}

				break;

		}

		if (0 !== queryLimits.length) {

			this.searchMessage = 'This search is limited by ' + queryLimits.join(', ');

		} else {

			this.searchMessage = '';

		}

		return query.getUrl();

	}


	/**
	 * Switch to the inactives screen.
	 */
	inactives(): void {

		this.savePageSettings();
		this.router.navigate(['/articles/inactives']);

	}


	/**
	 * This is used by the Sport radio button to
	 * turn the selected value a different color.
	 *
	 * @param name  The 'code' value for the radio button.
	 * @returns {any} A style object with content or empty.
	 */
	isHighlighted(name: string): any {

		if (name === this.currentSport) {

			return {'color': 'blue', 'font-weight': 'bold'};

		}

		return {};

	}


	/**
	 * This is used by the Status radio buttons
	 * to turn the selected value a different color.
	 *
	 * @param status  The status value for the radio button.
	 * @returns {any} A style object with content or empty.
	 */
	isStatusHighlighted(status: number): any {

		if (status === this.searchStatus) {

			if (-1 === status) {

				return {'color': 'red'};

			}

			return {'color': 'blue', 'font-weight': 'bold'};

		}

		return {};

	}


	/**
	 * Switch to the new post screen.
	 */
	newArticle(): void {

		this.savePageSettings();
		this.router.navigate(['/articles/item/new']);

	}


	/**
	 * When the user selects a Player in the
	 * player chooser, this stores the ID number for
	 * use later.
	 *
	 * @param event  The Player chosen by the user.
	 */
	onPlayerChange(event:any): void {

		let player:Player = new Player(event);
		this.playerSearchId = player.id;

	}


	/**
	 * When the user selects a Source in the
	 * source chooser, this stores the ID number for
	 * use later.
	 *
	 * @param event  The Source chosen by the user.
	 */
	onSourceChange(event:any): void {

		let source:Source = new Source(event);
		this.sourceSearchId = source.id;

	}


	/**
	 * When the user selects a Team in the
	 * team chooser, this stores the ID number for
	 * use later.
	 *
	 * @param event  The Team chosen by the user.
	 */
	onTeamChange(event:any): void {

		let team:Team = new Team(event);
		this.teamSearchId = team.id;

	}


	/**
	 * Carry out the article search.
	 *
	 */
	performSearch(): void {

		this.savePageSettings();
		this.newsSubscription = this.newsService.getNewsItems(this.searchNews.getValue()).subscribe(
			(response:News[]) => {

				this.news = response;
				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: (1 === response.length) ? '1 story found' : response.length.toString() + ' stories found',
					level: 1

				}));

			},
			(error:any) => {

				this.news = [];
				let msg:string = '';
				let msglevel:number = 1;

				if (error instanceof HttpErrorResponse && 404 !== error.status) {

					msg = 'Unable to complete query. Details: ' + Query.getQueryErrorMessage(error);
					msglevel = 4;

				} else {

					msg = 'No stories found'

				}

				this.msgSvc.sendMessageEntry(new MessageEntry({

					message: msg,
					level: msglevel

				}));

			}
		);

	}


	/**
	 * Used by list.component.html to reset
	 * the current page parameter.
	 */
	resetArticlesPerPage(): void {

		this.searchArticles(this.lastSearchType, true);

	}


	/**
	 * Save the current settings for the search on this page to session storage.
	 */
	savePageSettings(): void {

		let settings:PageSettings = new PageSettings();
		settings.currentPage = this.currentPage;
		settings.currentSport = this.currentSport;
		settings.initialized = true;
		settings.lastSearchType = this.lastSearchType;
		settings.perPageChoice = this.perPageChoice;
		settings.playerSearchId = this.playerSearchId;
		settings.searchByStoryId = this.searchByStoryId.nativeElement.value;
		settings.searchFullText = this.searchFullText.nativeElement.value;
		settings.searchOrderField = this.searchOrderField;
		settings.searchSortOrder = this.searchSortOrder;
		settings.searchStatus = this.searchStatus;
		settings.teamSearchId = this.teamSearchId;
		settings.typeaheadPlayerName = this.playerTypeahead.typeaheadPlayerNameInput.nativeElement.value;
		settings.typeaheadSource = this.sourceTypeahead.typeaheadSourceInput.nativeElement.value;
		settings.typeaheadTeamname = this.teamTypeahead.typeaheadTeamInput.nativeElement.value;
		settings.startDate = this.startDate.nativeElement.value;
		settings.endDate = this.endDate.nativeElement.value;
		settings.currentItemSport = this.currentItemSport;
		sessionStorage.setItem('pageSettings', JSON.stringify(settings));

	}


	/**
	 * Used by list.component.html to cause
	 * the search to be refreshed.
	 *
	 * @param {string}  type              The last search type.
	 * @param {boolean} resetCurrentPage  If true, set the current page to 1.
	 */
	searchArticles(type:string, resetCurrentPage:boolean): void {

		// Initialize the error settings.
		this.queryErrors = false;
		this.queryMessage = null;


		if (resetCurrentPage) {

			this.currentPage = 1;

		}

		// Get the search query URL.
		let q:string = this.getQuery(type);

		// Check to see if there was an error.
		if (this.queryErrors) {

			this.msgSvc.sendMessageEntry(this.queryMessage);

		} else {

			// Notify the user the search is starting.
			this.msgSvc.sendMessageEntry(new MessageEntry({

				message: 'Searching...',
				level: 1

			}));

			// Start the search.
			this.searchNews.next(q);
			this.performSearch();

		}

	}


	/**
	 * Used by list.component.html to pass
	 * in the current page parameter.
	 *
	 * @param {int} page  The page number to view.
	 */
	setCurrentPage( page: number ): void {

		this.currentPage = page;
		this.searchArticles(this.lastSearchType, false);

	}

	/**
	 * Show the versions of the current article.
	 *
	 * @param {number} id  The article ID to retrieve for.
	 */
	showVersions( id: number ): void {

		this.savePageSettings();
		this.router.navigate(['/articles/version', id]);

	}


	/**
	 * For a given user ID value, provide
	 * the user's name.
	 *
	 * @param {number} id  The User ID to look up.
	 * @return {string} The user name.
	 */
	userLookup(id:number):string {

		if (! this.isAdministrator) {
			return '';
		}

		let user:User = this.users.find( x => x.id === id);

		if (isNullOrUndefined( user )) {
			return '(unknown)';
		}

		return '(' + user.uname + ')';

	}

	handleEnterPress():void {
		console.log("Pressed enter")
		this.searchArticles('clear', true)
	}


}



// WEBPACK FOOTER //
// ./src/app/articles/list/list.component.ts